import {
    CLAIM_TYPES,
    LEGISLATION_KEYS,
    LEGISLATION_STATUSES,
} from './regulation-review-options'

const ReviewCheckStatus = Object.freeze({
    PASSED: 'passed',
    NEEDS_REVIEW: 'needs_review',
})

const isNonEmptyString = value => typeof value === 'string' && value.trim().length > 0

const createCheck = (id, passed, reason) => ({
    id,
    status: passed ? ReviewCheckStatus.PASSED : ReviewCheckStatus.NEEDS_REVIEW,
    reason,
})

const createFact = (id, value) => ({ id, value })

const defaultRegulationChecker = ({
    regulation,
    regulationIndex,
    legislations,
    exemptions,
}) => {
    const claims = Array.isArray(regulation.claims) ? regulation.claims : []
    const validClaims = claims.filter(claim => (
        claim && CLAIM_TYPES.includes(claim.type)
    ))
    const validExemptions = exemptions.filter(exemption => (
        exemption && isNonEmptyString(exemption.name)
    ))
    const hasDuplicateKey = isNonEmptyString(regulation.key)
        && legislations.some((item, index) => (
            index !== regulationIndex && item && item.key === regulation.key
        ))

    return {
        checks: [
            createCheck(
                'name',
                isNonEmptyString(regulation.name),
                {
                    messageKey: 'Component(ResearchReviewProcess):reason-confirm-legislation-name',
                    fallbackMessage: 'Confirm the legislation name.',
                },
            ),
            createCheck(
                'key',
                LEGISLATION_KEYS.includes(regulation.key),
                {
                    messageKey: 'Component(ResearchReviewProcess):reason-select-legislation-key',
                    fallbackMessage: 'Select the normalized legislation.',
                },
            ),
            createCheck(
                'status',
                LEGISLATION_STATUSES.includes(regulation.status),
                {
                    messageKey: 'Component(ResearchReviewProcess):reason-select-legislation-status',
                    fallbackMessage: 'Confirm how the document covers this legislation.',
                },
            ),
            createCheck(
                'unique-key',
                !hasDuplicateKey,
                {
                    messageKey: 'Component(ResearchReviewProcess):reason-select-unique-legislation',
                    fallbackMessage: 'Select a regulation that is not already used in another row.',
                },
            ),
            createCheck(
                'claims',
                validClaims.length === claims.length,
                {
                    messageKey: 'Component(ResearchReviewProcess):reason-confirm-claim-types',
                    fallbackMessage: 'Confirm the type of every legislation claim.',
                },
            ),
            createCheck(
                'exemptions',
                validExemptions.length === exemptions.length,
                {
                    messageKey: 'Component(ResearchReviewProcess):reason-confirm-exemptions',
                    fallbackMessage: 'Enter a name for every exemption.',
                },
            ),
        ],
        facts: [
            createFact('claims', claims.length),
            createFact('exemptions', exemptions.length),
            createFact('claims-with-evidence', claims.filter(claim => isNonEmptyString(claim.evidence)).length),
        ],
    }
}

const mergeEvaluation = (target, addition = {}) => ({
    checks: target.checks.concat(addition.checks || []),
    facts: target.facts.concat(addition.facts || []),
})

const createRegulationReviewCheckerRegistry = ({
    regulationCheckers = {},
    addons = [],
} = {}) => Object.freeze({
    evaluate(input) {
        const normalizedInput = {
            ...input,
            regulation: input.regulation || {},
            legislations: Array.isArray(input.legislations) ? input.legislations : [],
            exemptions: Array.isArray(input.exemptions) ? input.exemptions : [],
        }
        let evaluation = defaultRegulationChecker(normalizedInput)
        const legislationKey = normalizedInput.regulation.key
        const checker = legislationKey && typeof regulationCheckers[legislationKey] === 'function'
            ? regulationCheckers[legislationKey]
            : null

        if (checker) {
            evaluation = mergeEvaluation(evaluation, checker(normalizedInput))
        }

        evaluation = addons.reduce((result, addon) => (
            mergeEvaluation(result, addon(normalizedInput))
        ), evaluation)

        return {
            ...evaluation,
            status: evaluation.checks.some(check => check.status === ReviewCheckStatus.NEEDS_REVIEW)
                ? ReviewCheckStatus.NEEDS_REVIEW
                : ReviewCheckStatus.PASSED,
        }
    },
})

const regulationReviewCheckerRegistry = createRegulationReviewCheckerRegistry()

export {
    ReviewCheckStatus,
    createRegulationReviewCheckerRegistry,
    regulationReviewCheckerRegistry,
}

export default regulationReviewCheckerRegistry
